import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, errMsg } from "../../lib/api";
import { loadPrefs } from "../../lib/alerts";
import { PageHeader } from "../../components/common";
import { ReminderSchedule } from "../../components/ReminderSchedule";

const CH = [["email", "Email"], ["popup", "Pop-up"], ["sound", "Sound"]];
const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const inp = "bg-muted/60 border h-9 px-2 text-sm";

const TypeRow = ({ def, v, opts, set }) => (
  <tr data-testid={`alerts-row-${def.key}`}>
    <td className="min-w-[220px]"><div className="font-medium text-sm">{def.label}</div><div className="text-xs text-muted-foreground">{def.who}</div></td>
    {CH.map(([c]) => (
      <td key={c} className="text-center">{def.channels.includes(c)
        ? <input type="checkbox" data-testid={`alerts-${def.key}-${c}`} checked={!!v[c]} onChange={(e) => set(def.key, { [c]: e.target.checked })} className="h-4 w-4" />
        : <span className="text-muted-foreground">—</span>}</td>))}
    <td>{def.freq ? <select data-testid={`alerts-${def.key}-freq`} value={v.freq} onChange={(e) => set(def.key, { freq: e.target.value })} className={inp}>
      {Object.entries(opts[def.freq]).map(([k, l]) => <option key={k} value={k}>{l}</option>)}</select> : <span className="text-xs text-muted-foreground">—</span>}</td>
  </tr>
);

const Group = ({ g, label, d, f, set }) => (
  <div className="ax-panel mb-4" data-testid={`alerts-group-${g}`}>
    <div className="px-4 pt-4 pb-2 font-semibold text-sm">{label}</div>
    <div className="ax-scroll"><table className="ax-table w-full min-w-[720px]">
      <thead><tr><th>Reminder</th>{CH.map(([c, l]) => <th key={c} className="text-center w-20">{l}</th>)}<th className="w-56">How often</th></tr></thead>
      <tbody>{d.defs.filter((x) => x.group === g).map((x) => <TypeRow key={x.key} def={x} v={f.types[x.key]} opts={d.freq_options} set={set} />)}</tbody>
    </table></div>
  </div>
);

const Hours = ({ h, setH }) => (
  <div className="ax-panel p-4 mb-4" data-testid="alerts-hours">
    <div className="font-semibold text-sm">Working hours for pop-ups and sound</div>
    <p className="text-xs text-muted-foreground mb-3">Outside these hours nobody gets pop-ups or sounds. Emails follow their own times.</p>
    <div className="flex flex-wrap items-center gap-3">
      <label className="text-xs">From <input type="time" data-testid="alerts-hours-start" value={h.start} onChange={(e) => setH({ ...h, start: e.target.value })} className={inp} /></label>
      <label className="text-xs">To <input type="time" data-testid="alerts-hours-end" value={h.end} onChange={(e) => setH({ ...h, end: e.target.value })} className={inp} /></label>
      <div className="flex flex-wrap gap-1">{DAYS.map((l, i) => (
        <button key={l} data-testid={`alerts-day-${i}`} onClick={() => setH({ ...h, days: h.days.includes(i) ? h.days.filter((x) => x !== i) : [...h.days, i].sort() })}
          className={`h-9 px-3 text-xs border ${h.days.includes(i) ? "border-primary bg-primary/10" : "text-muted-foreground"}`}>{l}</button>))}</div>
    </div>
  </div>
);

export default function AdminAlerts() {
  const [d, setD] = useState(null);
  const [f, setF] = useState(null);
  const [busy, setBusy] = useState(false);
  const take = (x) => { setD(x); setF({ types: x.types, hours: x.hours }); };
  useEffect(() => { api.get("/admin/alerts").then((r) => take(r.data)).catch((e) => toast.error(errMsg(e))); }, []);
  if (!d || !f) return <div className="text-sm text-muted-foreground">Loading…</div>;
  const set = (k, v) => setF((x) => ({ ...x, types: { ...x.types, [k]: { ...x.types[k], ...v } } }));
  const dirty = JSON.stringify(f) !== JSON.stringify({ types: d.types, hours: d.hours });
  const save = () => { setBusy(true); api.put("/admin/alerts", f).then((r) => { take(r.data); loadPrefs(true).catch(() => {}); toast.success("Reminders saved for the whole team"); }).catch((e) => toast.error(errMsg(e))).finally(() => setBusy(false)); };
  return (
    <div className="max-w-5xl">
      <PageHeader eyebrow="Administration" title="Reminders & Alerts" desc="Choose, for the whole team, which reminders go out by email, which show a pop-up and which play a sound, and how often. Reminders are grouped: one per UID, one pop-up for everything pending. Each person can mute the sound for themselves.">
        <button data-testid="alerts-save" disabled={!dirty || busy} onClick={save} className="bg-primary text-primary-foreground h-10 px-5 text-sm disabled:opacity-50">Save changes</button>
      </PageHeader>
      {Object.entries(d.groups).map(([g, l]) => <Group key={g} g={g} label={l} d={d} f={f} set={set} />)}
      <Hours h={f.hours} setH={(h) => setF((x) => ({ ...x, hours: h }))} />
      <div className="font-semibold text-sm mt-8 mb-2">Email times</div>
      <ReminderSchedule />
    </div>
  );
}
